import { useState } from 'react';
import { useBrowseDecks } from '@/hooks/useBrowseDecks';
import { DeckCard } from '@/components/DeckCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Filter } from 'lucide-react';
import { CardSearchInput } from '@/components/CardSearchInput';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function BrowseDecks() {
  const [filters, setFilters] = useState({
    commanders: [] as string[],
    companion: '',
    cards: [] as string[],
    format: '',
  });
  const [searchInputs, setSearchInputs] = useState({
    commander1: '',
    commander2: '',
    companion: '',
    cardSearch: '',
    format: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } = useBrowseDecks(filters);

  const addCardFilter = (cardName: string) => {
    if (!filters.cards.includes(cardName)) {
      setFilters(prev => ({
        ...prev,
        cards: [...prev.cards, cardName]
      }));
    }
    setSearchInputs(prev => ({ ...prev, cardSearch: '' }));
  };

  const removeCardFilter = (cardName: string) => {
    setFilters(prev => ({
      ...prev,
      cards: prev.cards.filter(c => c !== cardName)
    }));
  };

  const applyFilters = () => {
    const commanders = [searchInputs.commander1, searchInputs.commander2].filter(Boolean);
    setFilters({
      commanders,
      companion: searchInputs.companion,
      cards: filters.cards,
      format: searchInputs.format === 'all' ? '' : searchInputs.format,
    });
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ commanders: [], companion: '', cards: [], format: '' });
    setSearchInputs({ commander1: '', commander2: '', companion: '', cardSearch: '', format: 'all' });
  };

  const allDecks = data?.pages.flatMap(page => page.decks) || [];

  if (isError) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Error loading decks. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Browse Decks</h1>
            <p className="text-sm sm:text-md text-muted-foreground">Discover the latest Magic: The Gathering decks</p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              showFilters && "bg-accent"
            )}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {(filters.commanders.length > 0 || filters.companion || filters.format || filters.cards.length > 0) && (
              <span className="ml-2 bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
                {filters.commanders.length + (filters.companion ? 1 : 0) + (filters.format && filters.format !== 'all' ? 1 : 0) + filters.cards.length}
              </span>
            )}
          </Button>
        </div>

        {showFilters && (
          <Card>
            <CardHeader>
              <CardTitle>Filter Decks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="commander1">Commander 1</Label>
                  <CardSearchInput
                    value={searchInputs.commander1}
                    onChange={(value) => setSearchInputs(prev => ({ ...prev, commander1: value }))}
                    onSelect={(cardName) => setSearchInputs(prev => ({ ...prev, commander1: cardName }))}
                    placeholder="Search commander..."
                    filterType="commander"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="commander2">Commander 2 (Optional)</Label>
                  <CardSearchInput
                    value={searchInputs.commander2}
                    onChange={(value) => setSearchInputs(prev => ({ ...prev, commander2: value }))}
                    onSelect={(cardName) => setSearchInputs(prev => ({ ...prev, commander2: cardName }))}
                    placeholder="Search second commander..."
                    filterType="commander"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="format">Format</Label>
                  <Select
                    value={searchInputs.format || undefined}
                    onValueChange={(value) => setSearchInputs(prev => ({ ...prev, format: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select format..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Formats</SelectItem>
                      <SelectItem value="commander">Commander</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="modern">Modern</SelectItem>
                      <SelectItem value="legacy">Legacy</SelectItem>
                      <SelectItem value="vintage">Vintage</SelectItem>
                      <SelectItem value="pioneer">Pioneer</SelectItem>
                      <SelectItem value="pauper">Pauper</SelectItem>
                      <SelectItem value="brawl">Brawl</SelectItem>
                      <SelectItem value="historic">Historic</SelectItem>
                      <SelectItem value="timeless">Timeless</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companion">Companion</Label>
                  <CardSearchInput
                    value={searchInputs.companion}
                    onChange={(value) => setSearchInputs(prev => ({ ...prev, companion: value }))}
                    onSelect={(cardName) => setSearchInputs(prev => ({ ...prev, companion: cardName }))}
                    placeholder="Search companion..."
                    filterType="companion"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Cards in Deck</Label>
                <div className="space-y-2">
                  <CardSearchInput
                    value={searchInputs.cardSearch}
                    onChange={(value) => setSearchInputs(prev => ({ ...prev, cardSearch: value }))}
                    onSelect={(cardName) => addCardFilter(cardName)}
                    placeholder="Search for cards to filter by..."
                  />

                  {filters.cards.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {filters.cards.map((cardName) => (
                        <div
                          key={cardName}
                          className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-sm flex items-center gap-2"
                        >
                          <span>{cardName}</span>
                          <button
                            onClick={() => removeCardFilter(cardName)}
                            className="hover:bg-secondary-foreground/20 rounded-full p-0.5"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={applyFilters}>Apply Filters</Button>
                <Button variant="outline" onClick={clearFilters}>Clear All</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-3 w-1/2 mb-4" />
                  <Skeleton className="h-20 w-full rounded-lg" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : allDecks.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">No decks found</p>
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {allDecks.map((deck) => (
                <DeckCard key={deck.event.id} deck={deck} />
              ))}
            </div>

            {hasNextPage && (
              <div className="flex justify-center">
                <Button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  variant="outline"
                >
                  {isFetchingNextPage ? 'Loading...' : 'Load More Decks'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}